/**
 * Configures the generated API client with auth interceptors.
 * Import this file once at app startup (e.g., in main.tsx).
 */
import { client } from './api/client.gen.js';
import { normalizeApiError } from '../services/api/errors.js';
import { API_BASE_URL, clearAuth, getAuthToken, refreshSession } from '../services/api/session.js';

// The generated client defaults to the OpenAPI spec's server URL (the developer's localhost). Every
// deployment points the SDK at its own API through VITE_API_URL, the same base the rest of the client uses.
// `credentials: 'include'`: the session's refresh token is an httpOnly cookie on /api/v1/auth, which the browser
// only sends and stores cross-origin when the request opts in.
client.setConfig({ baseUrl: API_BASE_URL, credentials: 'include' });

client.interceptors.request.use((request) => {
  const token = getAuthToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }

  // Team context: the server's policy middleware reads `x-team-id`, set by the TeamSelector;
  // an absent header means "All Teams".
  const orgStorage = localStorage.getItem('org-storage');
  if (orgStorage) {
    try {
      const { state } = JSON.parse(orgStorage);
      if (state?.activeTeamId) {
        request.headers.set('X-Team-Id', String(state.activeTeamId));
      }
    } catch {
      // Corrupted storage — skip team header
    }
  }
  return request;
});

// Guards against retrying the same original request more than once — defensive
// only, since we return a fetched Response directly rather than re-entering the
// interceptor pipeline, so this loop can't actually recurse today.
const retriedRequests = new WeakSet<Request>();

// A 401 refreshes the session through the httpOnly cookie (`refreshSession()`, serialized across tabs) and retries
// the original request once with the new access token. It signs out only when the server refuses the refresh; an
// unreachable server keeps the session (QG-CLI-COMP-001: this used to log out on every 401 with no refresh at all).
client.interceptors.response.use(async (response, request, options) => {
  if (response.status !== 401) return response;

  const isRefreshEndpoint = new URL(request.url).pathname.endsWith('/auth/refresh');
  if (isRefreshEndpoint || retriedRequests.has(request)) {
    clearAuth();
    return response;
  }

  let tokens: { token: string } | null;
  try {
    tokens = await refreshSession();
  } catch {
    // The server could not be reached: keep the session (it may be fine) and let the caller see the 401.
    return response;
  }
  if (!tokens) {
    clearAuth();
    return response;
  }

  retriedRequests.add(request);
  const retryHeaders = new Headers(request.headers);
  retryHeaders.set('Authorization', `Bearer ${tokens.token}`);
  const retryRequest = new Request(request.url, {
    method: request.method,
    headers: retryHeaders,
    body: options.serializedBody,
    redirect: 'follow',
  });
  const fetchImpl = options.fetch ?? globalThis.fetch;
  return fetchImpl(retryRequest);
});

// A rejected request throws an `ApiError` carrying the server's `status`, `code`, `details` and `field`, the same
// error `unwrapGenerated` makes for the hand-written services. The generated mutations run with `throwOnError`, which
// would otherwise throw the raw JSON body: `<Form>` then could not place a 409 on its field (`applyServerErrors`).
client.interceptors.error.use((error, response) =>
  error instanceof Error ? error : normalizeApiError(error, response?.status ?? 0),
);

/**
 * The `fetch` every streaming (SSE) call passes as its `fetch` option. The generated SSE client throws
 * `SSE failed: <status>` on a refused response and drops the body, and response interceptors do not run for streams,
 * so the person saw a generic failure. This reads the body first and throws the server's `ApiError` (message, code,
 * status), which reaches the call's `onSseError`.
 */
export const sseFetch: typeof globalThis.fetch = async (input, init) => {
  const response = await globalThis.fetch(input, init);
  if (response.ok) return response;
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON (a proxy page, an empty body): the status alone describes it.
  }
  throw normalizeApiError(body, response.status);
};

export { client };
